import { UntitledIcon } from '@faceup/icons'
import { ulDotsHorizontal } from '@faceup/icons/ulDotsHorizontal'
import { Link } from '@faceup/router'
import { Dropdown, useUiBaseConfig } from '@faceup/ui-base'
import { useWindowEvent } from '@mantine/hooks'
import { useContext, useRef, useState } from 'react'
import { UiContext } from '../UiProvider/UiProvider'
import type { AdminMenuItem as AdminMenuEntry } from './AdminMenu'
import { AdminMenuItem } from './AdminMenuItem'
import { resolveLandingKey, resolveSecondaryDefault } from './adminMenuUtils'
import { type AdminMenuSelection, isKeyboardActivation } from './useAdminMenuExpansion'

const overflowKey = 'more'

type AdminMenuRailOverflowProps = {
  items: AdminMenuEntry[]
  isExpanded: boolean
  isActive: boolean
  onItemHover: (key: string) => void
  onSelect: (selection: AdminMenuSelection) => void
}

export const AdminMenuRailOverflow = ({
  items,
  isExpanded,
  isActive,
  onItemHover,
  onSelect,
}: AdminMenuRailOverflowProps) => {
  const { adminMenu } = useContext(UiContext)
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const { direction } = useUiBaseConfig()

  // rc-menu swallows Escape to move focus around, it never closes the popup. The focus
  // restore has to outlast rc-menu's own handler, hence the timeout.
  useWindowEvent('keydown', event => {
    if (isOpen && event.key === 'Escape') {
      setIsOpen(false)
      setTimeout(() => triggerRef.current?.focus())
    }
  })

  const menuItems = items.flatMap(item => {
    const shared = {
      key: item.key,
      'data-test': `main-menu-${item.key}`,
      icon: <UntitledIcon icon={item.icon} size={16} />,
    }

    if (item.secondary) {
      const target = resolveSecondaryDefault(item.secondary)
      return target ? [{ ...shared, label: <Link to={target.link}>{item.label}</Link> }] : []
    }

    if (item.link) {
      return [{ ...shared, label: <Link to={item.link}>{item.label}</Link> }]
    }

    return [
      {
        ...shared,
        label: (
          <a href={item.href} onClick={item.onClick} target='_blank' rel='noreferrer'>
            {item.label}
          </a>
        ),
      },
    ]
  })

  return (
    <Dropdown
      open={isOpen}
      onOpenChange={setIsOpen}
      autoFocus
      // @ts-expect-error antd types Dropdown placements as top/bottom only, the side ones work
      placement={direction === 'rtl' ? 'leftTop' : 'rightTop'}
      menu={{
        items: menuItems,
        onClick: ({ key, domEvent }) =>
          onSelect({
            sectionKey: resolveLandingKey(items.find(item => item.key === key)),
            isKeyboard: isKeyboardActivation(domEvent),
            origin: 'popup',
          }),
      }}
    >
      <AdminMenuItem
        ref={triggerRef}
        itemKey={overflowKey}
        label={adminMenu?.more ?? 'More'}
        icon={ulDotsHorizontal}
        isExpanded={isExpanded}
        hasSecondary
        isActive={isActive || isOpen}
        onMouseEnter={() => onItemHover(overflowKey)}
        aria-haspopup='menu'
        aria-expanded={isOpen}
      />
    </Dropdown>
  )
}
